import { Component, EventEmitter, Injector, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { ClBaseComponent } from '../cl-base/cl-base.component';
import { ComponentType, ResourceType } from 'src/app/common/enumerations/enumerations';
import { utils } from 'src/app/modules/libs/utils';
import { Resource, ResourceRelation } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import { YesNoDialogComponent } from '../../yes-no-dialog/yes-no-dialog.component';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';
import { ChecklistsService } from 'src/app/components/checklists/checklists.service';
import { Checklist, RoleOption, Shielding } from 'src/app/components/checklists/checklists';
import { WFSectionLocalResource, WFStepLocalResource, WfTaskLocalResource } from 'src/app/components/workflow/workflow';

@Component({
  selector: 'cl-shieldings',
  templateUrl: './cl-shieldings.component.html',
  styleUrls: ['./cl-shieldings.component.scss']
})
export class ClShieldingsComponent extends ClBaseComponent implements OnInit, OnChanges, OnDestroy {

  @Output() checkShared = new EventEmitter<any>();

  shieldings?: Shielding[];
  shieldingsToDisplay?: Shielding[];
  shielding0?: Shielding;

  roles: Role[] = [];
  roles$!: Observable<Role[]>;
  rolesSubs!: Subscription;

  resource?: Resource = new Resource;
  resources: Resource[] = [];
  resources$!: Observable<Resource[]>;
  resourcesSubs!: Subscription;
  resourceMap = new Map<number | undefined, Resource>();

  resourceRelations: ResourceRelation[] = [];
  resourceRelationsFiltered: ResourceRelation[] = [];
  resourceRelations$!: Observable<ResourceRelation[]>;
  resourceRelationsSubs: Subscription = new Subscription;
  resourceRelationsMap = new Map<number | undefined, ResourceRelation>();

  lastCheckedTime: number | null = null;
  saveDelay = 2000; // 2 seconds
  defaultSaveDelay = 2000;
  saveTimer: ReturnType<typeof setTimeout> | null = null;

  shieldingUpdates: ShieldingUpdateResource[] = [];

  constructor(
    protected override injector: Injector,
    private service: ChecklistsService
  ) {
    super(injector);
  }
  override ngOnDestroy(): void {
    this.rolesSubs?.unsubscribe();
    this.resourcesSubs?.unsubscribe();
    this.resourceRelationsSubs?.unsubscribe();
  }

  ngOnInit() {

  }

  ngOnChanges(changes: SimpleChanges) {
    this.loadRoles();
    this.loadResources();
    if (this.builder || this.editor) {
      if ((changes['configuration'] || changes['options'])) {
        this.loadShieldingsFromConfiguration();
        // this.createShielding0();
      }
    }
    else {
      // if (this.signature && this.service.savingShieldings && this.service.savingTaskID == this.signature.id) {
      //   this.shieldingsToDisplay = this.service.savingShieldings;
      // }
      if (this.valueString != null) {
        if (changes['valueString'] || !this.shieldingsToDisplay?.length) {
          this.loadShieldingsFromValueString();
        }
      }
      else {
        this.loadShieldingsFromConfiguration();
        // this.createShielding0();
      }
    }
  }

  loadRoles() {
    this.roles$ = this.store.select(state => state.Roles.data);
    this.rolesSubs = this.roles$.subscribe(data => {
      this.roles = data;
    });
  }

  loadResources() {
    this.resources$ = this.store.select(state => state.Resources.data);
    this.resourcesSubs = this.resources$.subscribe(data => {
      if (data.length) {
        this.resources = data;
        this.resources.map(resource => this.resourceMap.set(resource.id, resource));
        this.loadResourceRelations();
      }
    });
  }

  loadResourceRelations() {
    this.resourceRelations$ = this.store.select(state => state.ResourceRelations.data);
    this.resourceRelationsSubs = this.resourceRelations$.subscribe(data => {
      if (data.length) {
        this.resourceRelations = data;
        this.resourceRelations.map(resourceRelation => this.resourceRelationsMap.set(resourceRelation.id, resourceRelation));
        if (!this.shieldingsToDisplay?.length) {
          this.loadValues();
        }
      }
    });
  }

  loadValues() {
    if (this.resources?.length && this.resourceRelations?.length)
      if (this.valueString)
        this.loadShieldingsFromValueString();
      else
        this.loadShieldingsFromConfiguration();
  }

  /**
   * Rehydrates saved shielding rows with current resource data and checkbox state.
   */
  loadShieldingsFromValueString() {
    this.service.savingShieldings = null;
    this.service.savingTaskID = null;

    const allShieldings = (utils.JSONparse(this.valueString) as Shielding[]) ?? [];
    this.shielding0 = allShieldings.find(s => s.id === 0);
    if (!this.shielding0) { this.createShielding0(); }
    const users = this.getUsers();
    if (allShieldings.length) {
      allShieldings.map(s => {
        const resource = this.resourceMap.get(this.resourceRelationsMap.get(s.id)?.childResourceID);
        if (resource) s.resource = resource;
        s.isShared = this.isSharedShielding(s);
        s.roleOptions?.map(o => {
          o.user = o.user ? o.user : users?.find(u => u.id === o.userID);
          o.disabled = this.checkboxDisabled(o, s);
        });
        s.waiting = false;
      });
      const shieldings = allShieldings.filter(s => s.id != 0);
      this.shieldings = shieldings;
      this.shielding0?.roleOptions?.map(o => o.disabled = this.checkboxDisabled(o, this.shielding0));
      this.shieldingsToDisplay = this.utils.cloneDeep(this.shieldings);
    }
    else { this.loadShieldingsFromConfiguration(); }
  }

  /**
   * Builds shielding rows from the configured location and accelerator resources.
   */
  loadShieldingsFromConfiguration() {
    const configuration: ShieldingConfiguration = utils.JSONparse(this.configuration);
    const resourceID = configuration?.location;
    if (resourceID) {
      this.resource = this.resources?.find(r => r.id == resourceID);
      const parent = this.resource?.parentResources?.find(x => x.parentResourceType == ResourceType.Accelerator);
      const parentResource = this.resources?.find(x => x.id == parent?.parentResourceID);
      this.shieldings = parentResource?.childResources?.filter(s => {
        const child = s.childResource ?? this.resourceMap.get(s.childResourceID);
        return s.childResourceType == ResourceType.Shielding && child?.checkboxVisible && utils.JSONparse(s.locationsIDs).includes(this.resource?.id);
      })
        .filter(x => {
          const child = x.childResource ?? this.resourceMap.get(x.childResourceID);
          return !configuration.longShutdown ? !child?.longShutdown : true;
        })
        .filter(x => {
          const child = x.childResource ?? this.resourceMap.get(x.childResourceID);
          return !configuration.inTunnel ? !child?.inTunnel : child?.inTunnel;
        })
        .sort((a, b) => this.utils.sort(a.position, b.position))
        .map(r => {
          const child = r.childResource ?? this.resourceMap.get(r.childResourceID);
          return { id: r.id, resourceID: child?.id, resource: child, roleOptions: this.getRoleOptions() } as Shielding;
        });
      this.shieldings?.map(s => {
        s.resource = this.resourceMap.get(this.resourceRelationsMap.get(s.id)?.childResourceID) as Resource;
        s.isShared = this.isSharedShielding(s);
        s.roleOptions?.map(o => {
          o.disabled = this.checkboxDisabled(o, s);
        });
      });
      this.shieldingsToDisplay = this.utils.cloneDeep(this.shieldings);
    }
  }

  createShielding0() {
    this.shielding0 = { id: 0, resourceID: 0, resource: { id: 0, type: 0 }, roleOptions: this.getRoleOptions() };
    this.shielding0?.roleOptions?.map(o => {
      o.disabled = this.checkboxDisabled(o, this.shielding0);
    });
  }

  getRoleOptions() {
    const options = utils.cloneDeep(this.options) as RoleOption[];
    options?.map(o => {
      if (!o.roleCodes) {
        o.roleCodes = this.roles.filter(x => o.roleIDs?.includes(x.id))?.map(r => r.code).join('/');
      }
    });
    return options;
  }

  /**
   * Builds the shared shielding tooltip from the other checklist tasks using the same shielding resource.
   * @param shielding Shielding row displayed in the checklist.
   */
  getSharedWithTooltip(shielding?: Shielding): string {
    const sharedWith = this.getSharedChecklistTaskNames(shielding).join('\n\n');
    return sharedWith || 'Shared shielding';
  }

  /**
   * Emits the related shared shielding task so the checklist can scroll to it.
   * @param event Click event from the Shared label.
   * @param shielding Shielding row displayed in the checklist.
   */
  goToSharedComponent(event: MouseEvent, shielding?: Shielding): void {
    event.stopPropagation();
    const sharedTask = this.getSharedChecklistTasks(shielding)[0];
    const shieldingResourceID = this.getShieldingResourceID(shielding);
    if (sharedTask?.task?.id && shieldingResourceID) {
      this.checkShared.emit({ taskID: sharedTask.task.id, shieldingResourceID });
    }
  }

  /**
   * Builds a stable row id so shared shielding navigation can scroll to the exact shielding item.
   * @param shielding Shielding row displayed in the checklist.
   */
  getShieldingRowID(shielding?: Shielding): string | null {
    const shieldingID = this.getShieldingResourceID(shielding);
    return this.taskID && shieldingID ? `shielding-${this.taskID}-${shieldingID}` : null;
  }

  /**
   * Resolves checklist task labels for other locations where this shielding is used.
   * @param shielding Shielding row used to find related resource relations.
   */
  private getSharedChecklistTaskNames(shielding?: Shielding): string[] {
    return this.getSharedChecklistTasks(shielding).map(sharedTask =>
      this.getChecklistTaskLabel(sharedTask.section, sharedTask.step, sharedTask.task)
    );
  }

  /**
   * Finds other checklist shielding tasks that use the same shared shielding.
   * @param shielding Shielding row used to find related resource relations.
   */
  private getSharedChecklistTasks(shielding?: Shielding): SharedShieldingTask[] {
    const checklist = this.checklist as Checklist | null;
    const locationIDs = this.getSharedLocationIDs(shielding);
    const sharedTasks: SharedShieldingTask[] = [];

    checklist?.wfTable?.wfTableLocal?.wfSectionLocals?.map(section => {
      section.wfStepLocals?.map(step => {
        step.wfTaskLocals?.filter(task => this.isSharedShieldingTask(task, shielding, locationIDs) && task.required).map(task => {
          sharedTasks.push({ section, step, task });
        });
      });
    });

    return sharedTasks;
  }

  /**
   * Determines whether a shielding resource is used by more than one checklist location.
   * @param shielding Shielding row whose resource relation should be checked.
   */
  private isSharedShielding(shielding?: Shielding): boolean {
    const locationIDs = this.getSharedLocationIDs(shielding, false);
    const relations = this.getShieldingRelations(shielding);

    // Shared pill follows the shielding resource, not the current checklist location.
    return locationIDs.length > 1 || relations.length > 1;
  }

  /**
   * Resolves shared location IDs, optionally excluding the current checklist location.
   * @param shielding Shielding row used to find related resource relations.
   * @param excludeCurrent Whether to omit the location rendered by this task.
   */
  private getSharedLocationIDs(shielding?: Shielding, excludeCurrent: boolean = true): number[] {
    const locationIDs: number[] = [];
    const currentLocationID = this.getCurrentLocationID();
    const relations = this.getShieldingRelations(shielding);

    relations?.map(relation => {
      // A shared shielding stores the checklist locations in each shielding relation.
      this.getRelationLocationIDs(relation).map(locationID => {
        if (locationID && (!excludeCurrent || locationID !== currentLocationID) && !locationIDs.includes(locationID)) {
          locationIDs.push(locationID);
        }
      });
    });

    return locationIDs;
  }

  /**
   * Merges hydrated resource relations with the global relation list for the shielding resource.
   * @param shielding Shielding row whose relation data should be collected.
   */
  private getShieldingRelations(shielding?: Shielding): ResourceRelation[] {
    const resourceID = this.getShieldingResourceID(shielding);
    const relations: ResourceRelation[] = [];
    const addRelation = (relation?: ResourceRelation | null) => {
      if (!relation) {
        return;
      }
      const alreadyAdded = relations.some(r => r.id ? r.id === relation.id : r === relation);
      if (!alreadyAdded) {
        relations.push(relation);
      }
    };

    shielding?.resource?.parentResources?.map(relation => addRelation(relation));
    this.resourceRelations
      .filter(relation => relation.childResourceID == resourceID)
      // Some shared shielding resources are only complete in the store relation list.
      .map(relation => addRelation(relation));

    return relations;
  }

  /**
   * Resolves the resource identifier from saved rows or freshly configured rows.
   * @param shielding Shielding row to normalize.
   */
  private getShieldingResourceID(shielding?: Shielding): number | undefined {
    return shielding?.resourceID ?? shielding?.resource?.id ?? this.resourceRelationsMap.get(shielding?.id)?.childResourceID;
  }

  /**
   * Checks whether a task is another shielding component that contains the same shared shielding.
   * @param task Checklist task candidate.
   * @param shielding Shared shielding row to match.
   * @param locationIDs Shared location identifiers to match.
   */
  private isSharedShieldingTask(task: WfTaskLocalResource, shielding: Shielding | undefined, locationIDs: number[]): boolean {
    const configuration: ShieldingConfiguration = utils.JSONparse(task.configuration);
    return task.id !== this.taskID &&
      task.componentID === ComponentType.ShieldingsComponent &&
      !!configuration?.location &&
      locationIDs.includes(configuration.location) &&
      this.taskContainsShielding(task, shielding);
  }

  /**
   * Checks saved task text first, then falls back to configuration/resource relations when the task has not saved shielding text yet.
   * @param task Checklist task candidate.
   * @param shielding Shared shielding row to match.
   */
  private taskContainsShielding(task: WfTaskLocalResource, shielding?: Shielding): boolean {
    const shieldingResourceID = this.getShieldingResourceID(shielding);
    // Shared link fallback needs both the row and its resource id to match task configuration.
    if (!shielding || !shieldingResourceID) {
      return false;
    }

    const savedShieldings = this.getTaskShieldings(task);
    if (savedShieldings.length) {
      return savedShieldings.some(savedShielding => this.getShieldingResourceID(savedShielding) === shieldingResourceID);
    }

    return this.taskConfigurationContainsShielding(task, shielding);
  }

  /**
   * Reads saved shielding rows from a task value.
   * @param task Checklist task candidate.
   */
  private getTaskShieldings(task: WfTaskLocalResource): Shielding[] {
    const shieldings = this.getValueObjects(task.text) as Shielding[];
    return Array.isArray(shieldings) ? shieldings : [];
  }

  /**
   * Uses shielding relations and task configuration to match unsaved shielding tasks.
   * @param task Checklist task candidate.
   * @param shielding Shared shielding row to match.
   */
  private taskConfigurationContainsShielding(task: WfTaskLocalResource, shielding: Shielding): boolean {
    const configuration: ShieldingConfiguration = utils.JSONparse(task.configuration);
    const shieldingResourceID = this.getShieldingResourceID(shielding);
    const shieldingResource = shielding.resource ?? this.resourceMap.get(shieldingResourceID);
    const relation = shieldingResource?.parentResources?.find(r => this.getRelationLocationIDs(r).includes(configuration?.location ?? 0)) ??
      this.resourceRelations.find(r => r.childResourceID == shieldingResourceID && this.getRelationLocationIDs(r).includes(configuration?.location ?? 0));

    if (!relation || !configuration?.location) {
      return false;
    }

    const longShutdownMatches = configuration.longShutdown || !shieldingResource?.longShutdown;
    const inTunnelMatches = configuration.inTunnel ? !!shieldingResource?.inTunnel : !shieldingResource?.inTunnel;
    return longShutdownMatches && inTunnelMatches;
  }

  /**
   * Gets location IDs from a resource relation regardless of whether they came as JSON or hydrated resources.
   * @param relation Shielding resource relation.
   */
  private getRelationLocationIDs(relation: ResourceRelation): number[] {
    const locationIDs = ((utils.JSONparse(relation.locationsIDs) as number[]) ?? []).filter(locationID => !!locationID);
    if (relation.parentResourceType === ResourceType.AcceleratorLocation && relation.parentResourceID && !locationIDs.includes(relation.parentResourceID)) {
      // Hydrated resource parent relations may carry the location as parentResourceID instead of locationsIDs.
      locationIDs.push(relation.parentResourceID);
    }
    relation.locations?.map(location => {
      if (location.id && !locationIDs.includes(location.id)) {
        locationIDs.push(location.id);
      }
    });
    return locationIDs;
  }

  /**
   * Formats the tooltip entry using the visible checklist section, step, and task title.
   * @param section Checklist section containing the shared task.
   * @param step Checklist step containing the shared task.
   * @param task Shared shielding task.
   */
  private getChecklistTaskLabel(section: WFSectionLocalResource, step: WFStepLocalResource, task: WfTaskLocalResource): string {
    const sectionName = this.stripTooltipHtml(section.name);
    const stepName = this.stripTooltipHtml(step.name);
    const taskTitle = this.stripTooltipHtml(task.name3 ?? task.name);
    return `${sectionName} - ${stepName} - ${taskTitle}`;
  }

  /**
   * Removes markup from labels before showing them in a plain text tooltip.
   * @param value Checklist label that may contain HTML formatting.
   */
  private stripTooltipHtml(value?: string | null): string {
    return (value ?? '').replace(/<[^>]*>/g, '').trim();
  }

  /**
   * Reads the current checklist location from the loaded resource or component configuration.
   */
  private getCurrentLocationID(): number | undefined {
    const configuration: ShieldingConfiguration = utils.JSONparse(this.configuration);
    return this.resource?.id ? this.resource.id : configuration?.location;
  }

  checkboxChanged(e: any, id?: number, sid: number | null = null) {
    this.service.savingTaskID = this.signature?.id;

    this.saveDelay = this.defaultSaveDelay;
    const currentUser = this.getCurrentUser(true) as User;
    currentUser.userRole = [];
    if (sid) {
      const shieldingToDisplay = this.shieldingsToDisplay?.find(s => s.id == sid);
      if (shieldingToDisplay) {
        shieldingToDisplay.waiting = true;
        // this.cdRef.detectChanges();
      }
      this.shieldingUpdates.push({ id: 0, optionID: id, checked: false, userID: 0 } as ShieldingUpdateResource);
      this.shieldingUpdates.push({ id: sid, optionID: id, checked: e.checked, userID: this.currentUser?.id } as ShieldingUpdateResource);
      this.service.savingShieldings = this.shieldingsToDisplay;
      this.saveData();
    }
    else {
      this.checkAll(id, e.checked);
    }
  }

  checkAll(id?: number, checked?: boolean) {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: this.getMessage(checked ? 'ChecklistShieldingCheckAllConfirmation' : 'ChecklistShieldingUncheckAllConfirmation').description,
        icon: 'stop'
      }
    });
    confirm.afterClosed().subscribe(data => {
      this.service.savingTaskID = this.signature?.id;

      const option0 = this.shielding0?.roleOptions?.find(o => o.id == id);
      if (data && option0) {
        this.shieldingUpdates = [];
        this.shieldingUpdates.push({ id: 0, optionID: id, checked, userID: this.currentUser?.id } as ShieldingUpdateResource);
        this.shieldings?.map(shielding => {
          const shieldingToDisplay = this.shieldingsToDisplay?.find(s => s.id == shielding?.id);
          if (shieldingToDisplay)
            shieldingToDisplay.waiting = true;
          this.service.savingShieldings = this.shieldingsToDisplay;

          const shieldingUpdate = { id: shielding.id, optionID: id, checked, userID: this.currentUser?.id } as ShieldingUpdateResource;
          this.shieldingUpdates.push(shieldingUpdate);
        });
        this.saveData();
      }
      else {
        this.loadShieldingsFromValueString();
      }
    });
  }

  checkboxDisabled(o: RoleOption, s?: Shielding) {
    const currentUser = this.getCurrentUser(true) as User;
    if (currentUser) {
      if ((s?.id ?? -1) >= 0) {
        const userSignedOption = s?.roleOptions?.find(o => o.userID == currentUser?.id);
        if (o.id == userSignedOption?.id) {
          return false;
        }
        if (userSignedOption) {
          return true;
        }
        if (!utils.intersect(o.roleIDs, currentUser.userRole?.map(r => r.roleID))) {
          return true;
        }
        if (this.shielding0?.roleOptions?.find(r => r.userID == currentUser.id)) {
          const id = this.shielding0?.roleOptions?.find(r => r.userID == currentUser.id)?.id;
          return o.id != id;
        }
        if (s?.id == 0) {
          const otherOptionsChecked = this.shieldings?.some(sh =>
            sh.roleOptions?.some(ro => ro.checked && ro.id != o.id && ro.userID == currentUser.id)
          );
          if (otherOptionsChecked) { return true; }
        }
        return (o.checked && o.userID) ? o.userID != currentUser.id : false;
      }
      else {
        if (o.userID == currentUser.id) {
          return false;
        }
        const otherOptionsChecked = this.shieldings?.some(sh =>
          sh.roleOptions?.some(ro => ro.checked && ro.id != o.id && ro.userID == currentUser.id)
        );
        if (otherOptionsChecked) { return true; }
        const roleAllowed = utils.intersect(o.roleIDs, currentUser.userRole?.map(r => r.roleID));
        if (roleAllowed && !o.checked) { return false; }
        return true;
      }
    }
    else { return true; }
  }

  saveData() {
    this.lastCheckedTime = Date.now();
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }
    if (this.lastCheckedTime) {
      this.saveTimer = setTimeout(() => {
        if (this.shieldingUpdates.length) {
          this.save.emit({ text: JSON.stringify(this.shieldingUpdates), refreshChecklist: false, refreshTask: true });
          this.shieldingUpdates = [];
          this.saveTimer = null;
        }
      }, this.saveDelay);
    }
  }
}





export class ShieldingConfiguration {
  location?: number;
  longShutdown?: boolean;
  inTunnel?: boolean;
}

export interface ShieldingUpdateResource {
  id: number;
  optionID: number;
  checked: boolean;
  userID: number;
}

interface SharedShieldingTask {
  section: WFSectionLocalResource;
  step: WFStepLocalResource;
  task: WfTaskLocalResource;
}
